import { memo, useMemo, useState } from "react";

import { filterBy, isPresent, noop, removeBy } from "neetocist";
import { Down, Up } from "neetoicons";
import { Button, Spinner } from "neetoui";
import PropTypes from "prop-types";
import { pluck, not } from "ramda";
import { useTranslation } from "react-i18next";

import DetailsBlock from "./DetailsBlock";
import "./session-environment.scss";

const SessionEnvironment = ({
  allowMultiline = false,
  details,
  handleCancel = noop,
  handleSubmit = noop,
  helpPopoverProps = {},
  iconSize = 20,
  isLoading = false,
  moreDetails = [],
  title = "",
}) => {
  const [showViewMore, setShowViewMore] = useState(false);

  const customViewMoreItems = useMemo(
    () => filterBy({ kind: "custom" }, moreDetails),
    [moreDetails]
  );

  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="w-full">
      <DetailsBlock
        {...{
          allowMultiline,
          details,
          handleCancel,
          handleSubmit,
          helpPopoverProps,
          iconSize,
          title,
        }}
        showIcons
      />
      {showViewMore && (
        <>
          <hr className="neeto-ui-border-gray-200 my-4" />
          <DetailsBlock
            {...{
              handleCancel,
              handleSubmit,
              helpPopoverProps,
              iconSize,
            }}
            details={removeBy({ kind: "custom" }, moreDetails)}
          />
          {isPresent(customViewMoreItems) && (
            <>
              <hr className="neeto-ui-border-gray-200 my-4" />
              {pluck("value", customViewMoreItems)}
            </>
          )}
        </>
      )}
      <Button
        className="mb-4"
        data-testid="view-more-less-button"
        icon={showViewMore ? Up : Down}
        style="link"
        label={
          showViewMore
            ? t("neetoMolecules.sessionEnvironment.viewLess")
            : t("neetoMolecules.sessionEnvironment.viewMore")
        }
        onClick={() => setShowViewMore(not)}
      />
    </div>
  );
};

SessionEnvironment.propTypes = {
  /*
    details can be an array of fields to be displayed.
  */
  details: PropTypes.array,
  /*
    handleCancel is a function that will be called when the cancel button is clicked.
  */
  handleCancel: PropTypes.func,
  /*
    handleSubmit is a function that will be called when the form is submitted.
  */
  handleSubmit: PropTypes.func,
  /*
    helpPopoverProps are the props that will be passed to the HelpPopover component.
  */
  helpPopoverProps: PropTypes.object,
  /*
    iconSize is a number that will be used to set the size of the icon.
  */
  iconSize: PropTypes.number,
  /*
    isLoading is a boolean that will be used to show a spinner when true.
  */
  isLoading: PropTypes.bool,
  /*
    moreDetails can be an array of fields to be displayed when the view more button is clicked.
  */
  moreDetails: PropTypes.array,
  /*
    The title to be displayed above the details.
  */
  title: PropTypes.string,
  /*
    allowMultiline is a boolean that will be used to decide whether the items should be displayed in a single line or multiple lines.
  */
  allowMultiline: PropTypes.bool,
};

export default memo(SessionEnvironment);
